import React, { useState, useEffect, useCallback } from "react";
import {
  Bitcoin,
  Coins,
  Building2,
  Check,
  Copy,
  ChevronLeft,
  ArrowDownToLine,
  ArrowUpFromLine,
  Send,
  Clock,
  AlertCircle,
  ExternalLink,
  Gift,
} from "lucide-react";
import DashboardLayout from "../components/dashboard/DashboardLayout";
import { cryptoMethods, bankDetails } from "../paymentConfig";
import { useAuth } from "../context/AuthContext";
import api from "../lib/api";

const formatMoney = (n) =>
  Number(n || 0).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 });

const CryptoIcon = ({ id, color }) => {
  if (id === "btc") return <Bitcoin size={18} strokeWidth={2} style={{ color }} />;
  return <Coins size={18} strokeWidth={2} style={{ color }} />;
};

const CopyField = ({ label, value, mono = true }) => {
  const [copied, setCopied] = useState(false);
  const onCopy = async () => {
    try {
      await navigator.clipboard.writeText(value);
    } catch {
      const ta = document.createElement("textarea");
      ta.value = value;
      document.body.appendChild(ta);
      ta.select();
      try { document.execCommand("copy"); } catch { /* noop */ }
      document.body.removeChild(ta);
    }
    setCopied(true);
    setTimeout(() => setCopied(false), 1800);
  };
  return (
    <div>
      <div className="text-[11px] text-white/55 mb-1.5">{label}</div>
      <div className="flex items-stretch gap-2 bg-white/[0.02] border border-white/8 rounded-xl overflow-hidden">
        <div className={`flex-1 min-w-0 px-4 py-3 text-[13px] text-white break-all ${mono ? "font-mono" : ""}`}>
          {value}
        </div>
        <button
          onClick={onCopy}
          className={`px-4 inline-flex items-center gap-1.5 text-[12px] font-medium border-l border-white/8 transition-colors ${
            copied ? "bg-emerald-500/15 text-emerald-400" : "text-white/70 hover:text-white hover:bg-white/[0.04]"
          }`}
        >
          {copied ? <Check size={13} /> : <Copy size={13} />}
          {copied ? "Copied" : "Copy"}
        </button>
      </div>
    </div>
  );
};

const QRCode = ({ data, size = 200 }) => {
  const url = `https://api.qrserver.com/v1/create-qr-code/?size=${size}x${size}&margin=12&data=${encodeURIComponent(data)}&bgcolor=ffffff&color=07080d`;
  return (
    <div className="p-2 bg-white rounded-2xl inline-block">
      <img src={url} alt="QR code" width={size} height={size} className="block" />
    </div>
  );
};

const StatusPill = ({ status }) => {
  const map = {
    Pending: "text-amber-400 bg-amber-400/10",
    Confirming: "text-amber-400 bg-amber-400/10",
    Completed: "text-emerald-400 bg-emerald-400/10",
    Rejected: "text-rose-400 bg-rose-400/10",
  };
  return (
    <span className={`text-[9px] uppercase tracking-wider font-semibold px-2 py-0.5 rounded ${map[status] || "text-white/55 bg-white/[0.05]"}`}>
      {status}
    </span>
  );
};

// ----- Crypto deposit sub-flow -----
const CryptoDeposit = ({ method, amount, onSubmitted, onBack }) => {
  const [txid, setTxid] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [err, setErr] = useState(null);

  const valid = txid.trim().length >= 12;

  const handleSubmit = async () => {
    setErr(null);
    setSubmitting(true);
    try {
      await api.post("/deposits", {
        method: method.id,
        method_label: method.label,
        amount: Number(amount),
        address: method.address,
        network: method.network,
        txid: txid.trim(),
      });
      setSubmitted(true);
      if (onSubmitted) onSubmitted();
    } catch (e) {
      setErr(e?.response?.data?.detail || "Could not submit deposit. Try again.");
    } finally {
      setSubmitting(false);
    }
  };

  if (submitted) {
    return (
      <div className="text-center py-10">
        <div className="w-16 h-16 rounded-full bg-emerald-500/15 border border-emerald-500/30 mx-auto flex items-center justify-center mb-4">
          <Check size={28} className="text-emerald-400" />
        </div>
        <div className="font-display font-bold text-[22px] text-white mb-2">Deposit submitted</div>
        <p className="text-white/55 text-[14px] mb-6 max-w-sm mx-auto">
          We've received your TX ID. Funds will reflect in your balance after we verify on-chain
          (usually within 10–30 minutes).
        </p>
        <button onClick={onBack} className="px-5 py-2.5 rounded-lg bb-blue text-white text-[13px] font-medium">Done</button>
      </div>
    );
  }

  return (
    <div>
      <button onClick={onBack} className="inline-flex items-center gap-1.5 text-[12px] text-white/55 hover:text-white mb-4">
        <ChevronLeft size={14} /> Change method
      </button>
      <div className="grid lg:grid-cols-[1fr_220px] gap-6 mb-6">
        <div className="space-y-4">
          <div className="flex items-center gap-3 px-4 py-3 rounded-xl bg-white/[0.02] border border-white/8">
            <div className="w-9 h-9 rounded-lg flex items-center justify-center" style={{ background: `${method.color}1f` }}>
              <CryptoIcon id={method.id} color={method.color} />
            </div>
            <div className="flex-1 min-w-0">
              <div className="text-[13px] text-white font-medium">{method.label}</div>
              <div className="text-[11px] text-white/55">Network · {method.network}</div>
            </div>
            <div className="text-right">
              <div className="text-[10px] uppercase tracking-wider text-white/45">Amount</div>
              <div className="text-[15px] text-white font-display font-bold tabular-nums">${formatMoney(amount)}</div>
            </div>
          </div>
          <CopyField label={`${method.short} deposit address`} value={method.address} />
          <div className="flex items-start gap-2 px-4 py-3 rounded-xl bg-amber-400/[0.06] border border-amber-400/20">
            <AlertCircle size={14} className="text-amber-400 shrink-0 mt-0.5" />
            <div className="text-[12px] text-amber-100/80 leading-relaxed">{method.note}</div>
          </div>
        </div>
        <div className="flex flex-col items-center lg:items-end">
          <QRCode data={method.address} size={180} />
          <div className="text-[10px] text-white/40 mt-2 text-center lg:text-right max-w-[180px]">
            Scan with your {method.short} wallet
          </div>
        </div>
      </div>

      <div className="border-t border-white/5 pt-5">
        <div className="text-[12px] text-white/55 mb-2">After sending payment, paste your TX ID</div>
        <input
          value={txid}
          onChange={(e) => setTxid(e.target.value)}
          placeholder={method.id === "btc" ? "e.g. 4a5e1e4baab89f3a32518a88c31bc87f..." : "e.g. 9d1c5a3f02e7d9b1...8c4b"}
          className="w-full h-11 px-4 bg-white/[0.02] border border-white/8 rounded-xl text-[13px] text-white font-mono outline-none focus:border-[#1c3df5] placeholder:text-white/30"
        />
        <div className="text-[10px] text-white/40 mt-1.5">
          Found in your sending wallet under transaction details / hash.
        </div>
        {err && (
          <div className="mt-3 flex items-start gap-2 px-3 py-2.5 rounded-lg bg-rose-500/10 border border-rose-500/30 text-[12px] text-rose-300">
            <AlertCircle size={14} className="shrink-0 mt-0.5" />
            <span>{err}</span>
          </div>
        )}
        <button
          onClick={handleSubmit}
          disabled={!valid || submitting}
          className={`mt-5 w-full py-3.5 rounded-xl font-medium text-white transition-all ${
            valid && !submitting ? "bb-blue" : "bg-white/[0.05] text-white/40 cursor-not-allowed"
          }`}
        >
          {submitting ? "Submitting..." : "Submit TX ID"}
        </button>
      </div>
    </div>
  );
};

// ----- Bank deposit sub-flow -----
const BankDeposit = ({ amount, ngnRate, onSubmitted, onBack }) => {
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [err, setErr] = useState(null);
  const ngnAmount = Math.ceil(Number(amount || 0) * Number(ngnRate || 1500));
  const formatNgn = (n) => Number(n || 0).toLocaleString("en-NG", { maximumFractionDigits: 0 });

  const handleSubmit = async () => {
    setErr(null);
    setSubmitting(true);
    try {
      await api.post("/deposits", {
        method: "bank",
        method_label: `Naira · ${bankDetails.bank}`,
        amount: Number(amount),
        bank_ref: `₦${formatNgn(ngnAmount)} @ ₦${formatNgn(ngnRate)}/USDT · ${bankDetails.beneficiary} · ${bankDetails.accountNumber}`,
      });
      setSubmitted(true);
      window.open(bankDetails.telegram, "_blank", "noopener,noreferrer");
      if (onSubmitted) onSubmitted();
    } catch (e) {
      setErr(e?.response?.data?.detail || "Could not submit. Try again.");
    } finally {
      setSubmitting(false);
    }
  };

  if (submitted) {
    return (
      <div className="text-center py-10">
        <div className="w-16 h-16 rounded-full bg-emerald-500/15 border border-emerald-500/30 mx-auto flex items-center justify-center mb-4">
          <Check size={28} className="text-emerald-400" />
        </div>
        <div className="font-display font-bold text-[22px] text-white mb-2">Awaiting confirmation</div>
        <p className="text-white/55 text-[14px] mb-2 max-w-sm mx-auto">
          Your bank deposit is pending. Please confirm payment on Telegram so we can credit your account.
        </p>
        <a
          href={bankDetails.telegram}
          target="_blank"
          rel="noopener noreferrer"
          className="inline-flex items-center gap-2 px-5 py-2.5 mt-4 rounded-lg bb-blue text-white text-[13px] font-medium"
        >
          <Send size={14} /> Open Telegram chat <ExternalLink size={12} />
        </a>
        <div className="mt-5">
          <button onClick={onBack} className="text-[12px] text-white/55 hover:text-white">Back to deposits</button>
        </div>
      </div>
    );
  }

  return (
    <div>
      <button onClick={onBack} className="inline-flex items-center gap-1.5 text-[12px] text-white/55 hover:text-white mb-4">
        <ChevronLeft size={14} /> Change method
      </button>
      <div className="flex items-center gap-3 px-4 py-3 rounded-xl bg-white/[0.02] border border-white/8 mb-5">
        <div className="w-9 h-9 rounded-lg flex items-center justify-center bg-[#1c3df5]/15 border border-[#1c3df5]/25">
          <Building2 size={17} className="text-[#7d8eff]" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="text-[13px] text-white font-medium">Bank Transfer (Naira)</div>
          <div className="text-[11px] text-white/55">{bankDetails.bank}</div>
        </div>
        <div className="text-right">
          <div className="text-[10px] uppercase tracking-wider text-white/45">USD equivalent</div>
          <div className="text-[15px] text-white font-display font-bold tabular-nums">${formatMoney(amount)}</div>
        </div>
      </div>

      {/* Big NGN amount banner */}
      <div
        data-testid="bank-deposit-ngn-banner"
        className="mb-5 p-5 rounded-2xl bg-gradient-to-br from-emerald-500/10 to-emerald-500/5 border border-emerald-500/30"
      >
        <div className="text-[11px] uppercase tracking-wider text-emerald-300/80 font-semibold mb-1">
          Send exactly this amount
        </div>
        <div className="flex items-baseline gap-2 flex-wrap">
          <div
            data-testid="bank-deposit-ngn-amount"
            className="font-display font-bold text-[34px] lg:text-[40px] text-emerald-300 tabular-nums leading-none"
          >
            ₦{formatNgn(ngnAmount)}
          </div>
          <div className="text-[12px] text-white/55 tabular-nums">
            = ${formatMoney(amount)} @ ₦{formatNgn(ngnRate)}/USDT
          </div>
        </div>
        <div className="text-[11px] text-white/55 mt-2">
          Rate locked at submission. Sending a different amount may delay confirmation.
        </div>
      </div>

      <div className="space-y-4 mb-5">
        <CopyField label="Beneficiary" value={bankDetails.beneficiary} mono={false} />
        <CopyField label="Account number" value={bankDetails.accountNumber} />
        <CopyField label="Bank" value={bankDetails.bank} mono={false} />
      </div>
      <div className="flex items-start gap-2 px-4 py-3 rounded-xl bg-amber-400/[0.06] border border-amber-400/20 mb-5">
        <AlertCircle size={14} className="text-amber-400 shrink-0 mt-0.5" />
        <div className="text-[12px] text-amber-100/80 leading-relaxed">
          Transfer <strong className="text-white">₦{formatNgn(ngnAmount)}</strong> to the account above (= ${formatMoney(amount)}). After payment, click below to confirm on Telegram and your account will be credited with ${formatMoney(amount)}.
        </div>
      </div>
      {err && (
        <div className="mb-3 flex items-start gap-2 px-3 py-2.5 rounded-lg bg-rose-500/10 border border-rose-500/30 text-[12px] text-rose-300">
          <AlertCircle size={14} className="shrink-0 mt-0.5" />
          <span>{err}</span>
        </div>
      )}
      <button
        onClick={handleSubmit}
        disabled={submitting}
        className="w-full py-3.5 rounded-xl font-medium text-white bb-blue inline-flex items-center justify-center gap-2 disabled:opacity-60"
      >
        <Send size={15} /> {submitting ? "Submitting..." : "I've paid — Confirm on Telegram"}
      </button>
      <div className="text-[10px] text-white/40 text-center mt-2">Telegram support: {bankDetails.telegramHandle}</div>
    </div>
  );
};

// ----- Withdraw -----
const WithdrawFlow = ({ balance, onSubmitted }) => {
  const [amount, setAmount] = useState(50);
  const [method, setMethod] = useState("crypto");
  const [destination, setDestination] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [err, setErr] = useState(null);

  const valid = amount > 0 && amount <= balance;

  const handleSubmit = async () => {
    setErr(null);
    setSubmitting(true);
    try {
      await api.post("/withdrawals", {
        method,
        amount: Number(amount),
        destination: destination.trim() || null,
      });
      setSubmitted(true);
      if (onSubmitted) onSubmitted();
    } catch (e) {
      setErr(e?.response?.data?.detail || "Could not submit withdrawal.");
    } finally {
      setSubmitting(false);
    }
  };

  if (submitted) {
    return (
      <div className="text-center py-10">
        <div className="w-16 h-16 rounded-full bg-emerald-500/15 border border-emerald-500/30 mx-auto flex items-center justify-center mb-4">
          <Check size={28} className="text-emerald-400" />
        </div>
        <div className="font-display font-bold text-[22px] text-white mb-2">Withdrawal requested</div>
        <p className="text-white/55 text-[14px] mb-5 max-w-sm mx-auto">
          ${formatMoney(amount)} pending. Reach out on Telegram to confirm your destination details.
        </p>
        <a
          href={bankDetails.telegram}
          target="_blank"
          rel="noopener noreferrer"
          className="inline-flex items-center gap-2 px-5 py-2.5 rounded-lg bb-blue text-white text-[13px] font-medium"
        >
          <Send size={14} /> Contact support <ExternalLink size={12} />
        </a>
        <div className="mt-4">
          <button onClick={() => setSubmitted(false)} className="text-[12px] text-white/55 hover:text-white">New withdrawal</button>
        </div>
      </div>
    );
  }

  return (
    <div>
      <div className="text-[12px] text-white/55 mb-2">Amount (USD)</div>
      <div className="relative mb-4">
        <span className="absolute left-4 top-1/2 -translate-y-1/2 text-white/55 text-[18px]">$</span>
        <input
          type="number"
          value={amount}
          onChange={(e) => setAmount(Number(e.target.value))}
          className="w-full h-14 pl-10 pr-4 bg-white/[0.02] border border-white/8 rounded-xl text-[22px] font-display font-bold text-white outline-none focus:border-[#1c3df5] tabular-nums"
        />
      </div>
      <div className="flex flex-wrap gap-2 mb-5">
        {[50, 100, 500, 1000, 5000].map((a) => (
          <button
            key={a}
            onClick={() => setAmount(a)}
            className={`px-3 py-1.5 rounded-lg text-[11px] border transition-all ${
              amount === a ? "border-[#1c3df5] bg-[#1c3df5]/15 text-white" : "border-white/8 text-white/55 hover:text-white"
            }`}
          >
            ${a.toLocaleString()}
          </button>
        ))}
      </div>

      <div className="text-[12px] text-white/55 mb-2">Withdraw to</div>
      <div className="grid grid-cols-2 gap-2 mb-4">
        <button
          onClick={() => setMethod("crypto")}
          className={`p-4 rounded-xl border text-left transition-all ${
            method === "crypto" ? "border-[#1c3df5] bg-[#1c3df5]/10" : "border-white/8 hover:border-white/20"
          }`}
        >
          <Coins size={17} className="text-white/80 mb-2" />
          <div className="text-[13px] font-medium text-white">Crypto wallet</div>
          <div className="text-[10px] text-white/45">USDT, TRX, or BTC</div>
        </button>
        <button
          onClick={() => setMethod("bank")}
          className={`p-4 rounded-xl border text-left transition-all ${
            method === "bank" ? "border-[#1c3df5] bg-[#1c3df5]/10" : "border-white/8 hover:border-white/20"
          }`}
        >
          <Building2 size={17} className="text-white/80 mb-2" />
          <div className="text-[13px] font-medium text-white">Bank transfer</div>
          <div className="text-[10px] text-white/45">Naira (NGN)</div>
        </button>
      </div>

      <div className="text-[12px] text-white/55 mb-2">Destination ({method === "crypto" ? "wallet address" : "bank account"}) — optional, confirm on Telegram</div>
      <input
        value={destination}
        onChange={(e) => setDestination(e.target.value)}
        placeholder={method === "crypto" ? "Your wallet address" : "Bank account details"}
        className="w-full h-11 px-3 mb-5 bg-white/[0.02] border border-white/8 rounded-xl text-[13px] text-white outline-none focus:border-[#1c3df5] placeholder:text-white/30 font-mono"
      />

      <div className="flex justify-between text-[13px] py-3 border-y border-white/5 mb-5">
        <span className="text-white/55">Available balance</span>
        <span className="text-white tabular-nums">${formatMoney(balance)}</span>
      </div>

      {err && (
        <div className="mb-3 flex items-start gap-2 px-3 py-2.5 rounded-lg bg-rose-500/10 border border-rose-500/30 text-[12px] text-rose-300">
          <AlertCircle size={14} className="shrink-0 mt-0.5" />
          <span>{err}</span>
        </div>
      )}
      <button
        onClick={handleSubmit}
        disabled={!valid || submitting}
        className={`w-full py-3.5 rounded-xl font-medium text-white transition-all inline-flex items-center justify-center gap-2 ${
          valid && !submitting ? "bb-blue" : "bg-white/[0.05] text-white/40 cursor-not-allowed"
        }`}
      >
        {submitting ? "Submitting..." : "Submit withdrawal request"}
      </button>
      <div className="text-[10px] text-white/40 text-center mt-2">
        Withdrawals are reviewed and processed manually via Telegram support.
      </div>
    </div>
  );
};

// ----- Main page -----
const Funds = () => {
  const { user, refreshMe } = useAuth();
  const [tab, setTab] = useState("deposit");
  const [methodChoice, setMethodChoice] = useState(null);
  const [amount, setAmount] = useState(50);
  const [stage, setStage] = useState("method");
  const [deposits, setDeposits] = useState([]);
  const [withdrawals, setWithdrawals] = useState([]);
  const [ngnRate, setNgnRate] = useState(1500);

  const loadTx = useCallback(async () => {
    try {
      const [d, w] = await Promise.all([api.get("/deposits"), api.get("/withdrawals")]);
      setDeposits(d.data || []);
      setWithdrawals(w.data || []);
    } catch { /* noop */ }
  }, []);

  useEffect(() => {
    loadTx();
    refreshMe();
    api.get("/settings").then(({ data }) => {
      if (data?.usdt_ngn_rate) setNgnRate(Number(data.usdt_ngn_rate));
    }).catch(() => { /* keep default */ });
  }, [loadTx, refreshMe]);

  const resetFlow = () => {
    setStage("method");
    setMethodChoice(null);
    loadTx();
    refreshMe();
  };

  const cryptoMethod = cryptoMethods.find((m) => m.id === methodChoice);

  const balance = Number(user?.balance || 0);
  const transactions = [
    ...deposits.map((d) => ({
      id: d.id,
      type: d.method === "bank" ? "Bank Transfer" : "Crypto Deposit",
      method: d.method_label || d.method,
      amount: Number(d.amount),
      txid: d.txid,
      status: d.status,
      date: d.created_at,
    })),
    ...withdrawals.map((w) => ({
      id: w.id,
      type: "Withdrawal",
      method: w.method,
      amount: -Number(w.amount),
      status: w.status,
      date: w.created_at,
    })),
  ].sort((a, b) => new Date(b.date) - new Date(a.date));

  return (
    <DashboardLayout title="Funds" subtitle="Deposit & withdraw">
      <div className="grid lg:grid-cols-3 gap-4 lg:gap-5">
        <div className="lg:col-span-2">
          <div className="bb-card rounded-2xl p-5 lg:p-6">
            <div className="grid grid-cols-2 gap-2 mb-6">
              <button
                onClick={() => { setTab("deposit"); resetFlow(); }}
                className={`flex items-center justify-center gap-2 py-3 rounded-xl border transition-all ${
                  tab === "deposit" ? "border-[#1c3df5] bg-[#1c3df5]/15 text-white" : "border-white/8 text-white/55 hover:text-white"
                }`}
              >
                <ArrowDownToLine size={16} /> Deposit
              </button>
              <button
                onClick={() => { setTab("withdraw"); resetFlow(); }}
                className={`flex items-center justify-center gap-2 py-3 rounded-xl border transition-all ${
                  tab === "withdraw" ? "border-[#1c3df5] bg-[#1c3df5]/15 text-white" : "border-white/8 text-white/55 hover:text-white"
                }`}
              >
                <ArrowUpFromLine size={16} /> Withdraw
              </button>
            </div>

            {tab === "withdraw" && <WithdrawFlow balance={balance} onSubmitted={loadTx} />}

            {tab === "deposit" && stage === "method" && (
              <div>
                {/* First-deposit bonus banner — only if user hasn't gotten it yet */}
                {!user?.bonus_credited && (
                  <div
                    data-testid="bonus-banner"
                    className="mb-5 p-4 lg:p-5 rounded-2xl relative overflow-hidden border border-emerald-400/35"
                    style={{ background: "linear-gradient(135deg, rgba(16,185,129,0.18) 0%, rgba(16,185,129,0.06) 100%)" }}
                  >
                    <div className="absolute -top-8 -right-8 w-40 h-40 rounded-full bg-emerald-400/20 blur-3xl"></div>
                    <div className="relative flex items-start gap-3">
                      <div className="w-10 h-10 rounded-xl bg-emerald-500/25 border border-emerald-400/40 flex items-center justify-center shrink-0">
                        <Gift size={18} className="text-emerald-300" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 flex-wrap mb-0.5">
                          <span className="font-display font-bold text-[15px] lg:text-[16px] text-white">100% First-Deposit Bonus</span>
                          <span className="text-[9px] uppercase tracking-wider px-1.5 py-0.5 bg-emerald-400/20 border border-emerald-400/40 rounded text-emerald-200 font-semibold">
                            Limited
                          </span>
                        </div>
                        <div className="text-[12px] text-emerald-100/85 leading-relaxed">
                          Deposit <strong className="text-white">$20 or more</strong> and we'll match it 100% — instantly doubling your trading balance. Applies to your first approved deposit only.
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                <div className="text-[12px] text-white/55 mb-3">Choose payment method</div>
                <div className="space-y-2 mb-2">
                  {cryptoMethods.map((m) => (
                    <button
                      key={m.id}
                      onClick={() => { setMethodChoice(m.id); setStage("amount"); }}
                      className="w-full flex items-center gap-3 px-4 py-3.5 rounded-xl border border-white/8 hover:border-[#1c3df5]/40 hover:bg-white/[0.03] transition-all text-left"
                    >
                      <div className="w-10 h-10 rounded-lg flex items-center justify-center shrink-0" style={{ background: `${m.color}22` }}>
                        <CryptoIcon id={m.id} color={m.color} />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="text-[14px] text-white font-medium">{m.label}</div>
                        <div className="text-[11px] text-white/45">Network · {m.network} · Fee 0%</div>
                      </div>
                      <ChevronLeft size={15} className="rotate-180 text-white/40" />
                    </button>
                  ))}
                  <button
                    onClick={() => { setMethodChoice("bank"); setStage("amount"); }}
                    className="w-full flex items-center gap-3 px-4 py-3.5 rounded-xl border border-white/8 hover:border-[#1c3df5]/40 hover:bg-white/[0.03] transition-all text-left"
                  >
                    <div className="w-10 h-10 rounded-lg flex items-center justify-center shrink-0 bg-[#1c3df5]/15">
                      <Building2 size={17} className="text-[#7d8eff]" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="text-[14px] text-white font-medium">Bank Transfer (Naira)</div>
                      <div className="text-[11px] text-white/45">{bankDetails.bank} · Confirm via Telegram</div>
                    </div>
                    <ChevronLeft size={15} className="rotate-180 text-white/40" />
                  </button>
                </div>
                <div className="mt-5 flex items-start gap-2 px-4 py-3 rounded-xl bg-white/[0.02] border border-white/5">
                  <Clock size={13} className="text-white/45 shrink-0 mt-0.5" />
                  <div className="text-[11px] text-white/55 leading-relaxed">
                    Crypto deposits credit within 10–30 minutes after on-chain confirmation. Bank transfers are confirmed manually via Telegram within minutes during business hours.
                  </div>
                </div>
              </div>
            )}

            {tab === "deposit" && stage === "amount" && (
              <div>
                <button onClick={() => { setStage("method"); setMethodChoice(null); }} className="inline-flex items-center gap-1.5 text-[12px] text-white/55 hover:text-white mb-4">
                  <ChevronLeft size={14} /> Change method
                </button>
                <div className="text-[12px] text-white/55 mb-2">
                  Amount ({methodChoice === "bank" ? "USD equivalent" : "USD"})
                </div>
                <div className="relative mb-3">
                  <span className="absolute left-4 top-1/2 -translate-y-1/2 text-white/55 text-[18px]">$</span>
                  <input
                    type="number"
                    value={amount}
                    onChange={(e) => setAmount(Number(e.target.value))}
                    data-testid="deposit-amount-input"
                    className="w-full h-14 pl-10 pr-4 bg-white/[0.02] border border-white/8 rounded-xl text-[22px] font-display font-bold text-white outline-none focus:border-[#1c3df5] tabular-nums"
                  />
                </div>

                {/* Real-time bonus preview */}
                {!user?.bonus_credited && amount > 0 && (
                  amount >= 20 ? (
                    <div
                      data-testid="bonus-preview-eligible"
                      className="mb-3 px-4 py-3 rounded-xl border border-emerald-400/35"
                      style={{ background: "linear-gradient(135deg, rgba(16,185,129,0.15) 0%, rgba(16,185,129,0.05) 100%)" }}
                    >
                      <div className="flex items-center gap-2 mb-1">
                        <Gift size={14} className="text-emerald-300" />
                        <span className="text-[12px] font-semibold text-emerald-200">100% bonus unlocked</span>
                      </div>
                      <div className="flex items-baseline gap-2 flex-wrap">
                        <span className="font-display font-bold text-[22px] text-emerald-300 tabular-nums leading-none">
                          +${formatMoney(amount)} bonus
                        </span>
                        <span className="text-[11px] text-white/55 tabular-nums">
                          → ${formatMoney(amount * 2)} credited to your account
                        </span>
                      </div>
                    </div>
                  ) : (
                    <div
                      data-testid="bonus-preview-ineligible"
                      className="mb-3 px-4 py-2.5 rounded-xl bg-white/[0.03] border border-white/10"
                    >
                      <div className="flex items-center gap-2 text-[12px] text-white/65">
                        <Gift size={13} className="text-white/45" />
                        <span>
                          Deposit <strong className="text-emerald-300">$20</strong> or more to unlock the 100% bonus
                          (${formatMoney(Math.max(0, 20 - amount))} away).
                        </span>
                      </div>
                    </div>
                  )
                )}

                {methodChoice === "bank" && amount > 0 && (
                  <div
                    data-testid="deposit-ngn-preview"
                    className="mb-3 px-4 py-3 rounded-xl bg-emerald-500/8 border border-emerald-500/25"
                  >
                    <div className="flex items-baseline justify-between gap-3 flex-wrap">
                      <div>
                        <div className="text-[10px] uppercase tracking-wider text-emerald-300/80 font-semibold mb-0.5">
                          You'll pay
                        </div>
                        <div className="font-display font-bold text-[22px] text-emerald-300 tabular-nums leading-none">
                          ₦{Math.ceil(Number(amount) * Number(ngnRate)).toLocaleString("en-NG")}
                        </div>
                      </div>
                      <div className="text-[11px] text-white/55 tabular-nums">
                        @ ₦{Number(ngnRate).toLocaleString("en-NG")}/USDT
                      </div>
                    </div>
                  </div>
                )}

                <div className="flex flex-wrap gap-2 mb-6">
                  {[10, 50, 100, 250, 500, 1000, 5000].map((a) => (
                    <button
                      key={a}
                      onClick={() => setAmount(a)}
                      className={`px-3 py-1.5 rounded-lg text-[11px] border transition-all ${
                        amount === a ? "border-[#1c3df5] bg-[#1c3df5]/15 text-white" : "border-white/8 text-white/55 hover:text-white"
                      }`}
                    >
                      ${a.toLocaleString()}
                    </button>
                  ))}
                </div>
                <button
                  onClick={() => setStage("pay")}
                  disabled={amount <= 0}
                  className={`w-full py-3.5 rounded-xl font-medium text-white transition-all ${
                    amount > 0 ? "bb-blue" : "bg-white/[0.05] text-white/40 cursor-not-allowed"
                  }`}
                >
                  Continue
                </button>
              </div>
            )}

            {tab === "deposit" && stage === "pay" && cryptoMethod && (
              <CryptoDeposit method={cryptoMethod} amount={amount} onSubmitted={loadTx} onBack={resetFlow} />
            )}
            {tab === "deposit" && stage === "pay" && methodChoice === "bank" && (
              <BankDeposit amount={amount} ngnRate={ngnRate} onSubmitted={loadTx} onBack={resetFlow} />
            )}
          </div>
        </div>

        <div className="space-y-4 lg:space-y-5">
          <div className="bb-card rounded-2xl p-5 lg:p-6">
            <div className="text-[12px] text-white/55 mb-1">Available balance</div>
            <div className="font-display text-[28px] font-bold text-white tabular-nums mb-1">${formatMoney(balance)}</div>
            <div className="text-[11px] text-white/45 mb-5">{user?.email}</div>
            <div className="flex items-start gap-2 px-3 py-2.5 rounded-xl bg-[#1c3df5]/[0.08] border border-[#1c3df5]/20">
              <Send size={13} className="text-[#7d8eff] shrink-0 mt-0.5" />
              <div className="text-[11px] text-white/65 leading-relaxed">
                Need help? Message us on Telegram:{" "}
                <a href={bankDetails.telegram} target="_blank" rel="noopener noreferrer" className="text-[#7d8eff] hover:underline font-medium">
                  {bankDetails.telegramHandle}
                </a>
              </div>
            </div>
          </div>

          <div className="bb-card rounded-2xl p-5 lg:p-6">
            <div className="text-[12px] text-white/55 mb-3">Recent transactions</div>
            {transactions.length === 0 ? (
              <div className="text-[12px] text-white/40 py-4">No transactions yet.</div>
            ) : (
              <div className="space-y-2 max-h-[420px] overflow-y-auto pr-1">
                {transactions.map((t) => {
                  const isDep = t.amount >= 0;
                  return (
                    <div key={t.id} className="flex items-start gap-3 py-2.5 border-b border-white/5 last:border-0">
                      <div className={`w-8 h-8 rounded-lg flex items-center justify-center shrink-0 ${isDep ? "bg-emerald-500/10 text-emerald-400" : "bg-rose-500/10 text-rose-400"}`}>
                        {isDep ? <ArrowDownToLine size={14} /> : <ArrowUpFromLine size={14} />}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-0.5 flex-wrap">
                          <span className="text-[12px] text-white font-medium truncate">{t.type}</span>
                          {t.status && <StatusPill status={t.status} />}
                        </div>
                        <div className="text-[10px] text-white/45 truncate">
                          {t.method} · {new Date(t.date).toLocaleString(undefined, { dateStyle: "short", timeStyle: "short" })}
                        </div>
                        {t.txid && <div className="text-[10px] text-white/35 font-mono truncate mt-0.5">TX: {t.txid.slice(0, 16)}…</div>}
                      </div>
                      <div className="text-right shrink-0">
                        <div className={`text-[13px] tabular-nums font-semibold ${isDep ? "text-emerald-400" : "text-rose-400"}`}>
                          {isDep ? "+" : ""}${formatMoney(Math.abs(t.amount))}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      </div>
    </DashboardLayout>
  );
};

export default Funds;
